import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toastError, toastSuccess } from "helpers";
import { createLogger } from "logging";
import { useApi, useBoardSettings, useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { BoardTemplatesContext } from "./BoardTemplatesContext";
import { BoardTemplatesPanel } from "./components/BoardTemplatesPanel";

const logger = createLogger("BoardTemplatesProvider");

export const BoardTemplatesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();
  const { templatesApi } = useApi();
  const { boardId, reloadBoardSettings } = useBoardSettings();
  const queryClient = useQueryClient();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);

  const templatesKey = useMemo(() => ["board-templates", boardId], [boardId]);
  const validNamesKey = useMemo(() => ["board-templates-validNames"], []);

  const queryResult = useQuery({
    queryKey: templatesKey,
    queryFn: async ({ signal }) => {
      const data = await templatesApi.getTemplates(boardId!, signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!boardId,
  });
  const results = queryResult.data;
  const isLoading = queryResult.isFetching;

  const validNamesQueryResult = useQuery({
    queryKey: validNamesKey,
    queryFn: async ({ signal }) => {
      const data = await templatesApi.getValidTemplateNames(signal);
      logger.trace("Loaded", data);
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });
  const validNames = useMemo(() => validNamesQueryResult.data || [], [validNamesQueryResult.data]);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: templatesKey }),
    [queryClient, templatesKey]
  );

  const uploadMutation = useMutation({
    mutationFn: ({ file, language }: { file: File; language?: string }) => {
      return templatesApi.uploadTemplate(boardId!, file, language);
    },
    onSuccess: () => {
      toastSuccess(i18n.BoardTemplate_Upload_Success);
      invalidate();
    },
    onError: (err: unknown) => toastError(i18n.BoardTemplate_Upload_Error, err),
  });

  const removeMutation = useMutation({
    mutationFn: (path: string) => {
      return templatesApi.removeTemplate(boardId!, path);
    },
    onSuccess: () => {
      toastSuccess(i18n.BoardTemplate_Delete_Success);
      invalidate();
    },
    onError: (err: unknown) => toastError(i18n.BoardTemplate_Delete_Error.replace("{0}", i18n.File), err),
    onSettled: () => {
      setDeletingPath(null);
    },
  });

  const upload = useCallback(
    async (file: File, language?: string) => {
      await uploadMutation.mutateAsync({ file, language });
    },
    [uploadMutation]
  );

  const remove = useCallback(
    async (path: string) => {
      setDeletingPath(path);
      await removeMutation.mutateAsync(path);
    },
    [removeMutation]
  );

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    reloadBoardSettings();
  }, [reloadBoardSettings]);

  const contextValue = useMemo(
    () => ({
      results: results || [],
      validNames,
      isLoading,
      isPanelOpen,
      openPanel,
      closePanel,
      upload,
      remove,
      isUploading: uploadMutation.isPending,
      deletingPath,
    }),
    [
      results,
      validNames,
      isLoading,
      isPanelOpen,
      openPanel,
      closePanel,
      upload,
      remove,
      uploadMutation.isPending,
      deletingPath,
    ]
  );

  return (
    <BoardTemplatesContext.Provider value={contextValue}>
      {children}
      {isPanelOpen && <BoardTemplatesPanel />}
    </BoardTemplatesContext.Provider>
  );
};
